import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { Form, Formik } from 'formik';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormError } from '@/components/ui/form-field';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { Spinner } from '@/components/ui/spinner';
import { Typography, typographyVariants } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { Variants } from '@/types/common';
import { IconComponent } from '@/util/icons';
import { showAlert } from '@/slice/AlertSlice';
import { testVendorConnectionSyncAPI } from '../../pms-configuration/service/configurationApiService';
import {
  connectedConnection,
  failedConnection,
} from '../../pms-configuration/types/connection-state';
import connectionFailed from '@/asset/images/connection-failed.jpg';
import { CredentialSummary, credentialRows } from '../../components/CredentialSummary';
import { FormikDirtyReporter } from './FormikDirtyReporter';
import { SubmitRefCleanup } from './SubmitRefCleanup';
import { credentialKindFor } from '../credentialKind';
import { integrationStatusCopy as copy } from '../status/copy';
import type { ConnectionPanelProps } from '../types';

/** Spec §5.4: key/secret are required only when nothing is stored for them. */
const buildSchema = (stored: { key: boolean; secret: boolean }) =>
  yup.object().shape({
    url: yup.string().required('Please enter subdomain'),
    key: stored.key ? yup.string() : yup.string().required('Please enter API Key'),
    secret: stored.secret ? yup.string() : yup.string().required('Please enter API Secret'),
  });

const errorCls = 'border-l-2 border-destructive pl-2';

/** Renders a labeled input field with validation error display. Caller is
 *  responsible for gating `error` on touched (pass undefined until then). */
const FormField = ({
  id, label, type = 'text', placeholder, value, error, onChange, connected, required = true,
}: {
  id: string; label: string; type?: string; placeholder: string;
  value: string; error?: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  connected: boolean; required?: boolean;
}) => (
  <div className={`mb-3 ${connected ? '' : errorCls}`}>
    <Label htmlFor={id} required={required}>{label}</Label>
    <Input id={id} name={id} type={type} placeholder={placeholder} value={value}
      onChange={onChange} className={error ? 'border-destructive' : ''} data-testid={`${id}_id`} />
    <FormError message={error} />
  </div>
);

/**
 * Rentvine's credentials panel. Read mode renders the masked
 * `CredentialSummary`; edit mode the Formik form with key/secret blank
 * (paste to replace). Its own effect re-runs the connection test whenever
 * `ctx.vendorData` changes (the bootstrap GET now lives in
 * `useVendorSettings`, not here).
 */
const RentvineConnectionPanel = ({ ctx }: ConnectionPanelProps) => {
  const {
    vendor,
    account,
    vendorData,
    setConnectionState,
    connected,
    setConnected,
    submitRef,
    submitCredentials,
    redirectToHowToConfigure,
    formSubmitAttempted,
    setToggleLoader,
    editedSettings,
    mode,
    credentialsFormKey,
    pendingCredentials,
    setCredentialsDirty,
  } = ctx;
  const dispatch = useDispatch();
  const [showModel, setShowModel] = useState(false);
  const [loading, setLoading] = useState(false);
  const vendorDisplayName = vendor.displayName;

  useEffect(() => {
    if (!vendorData) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setToggleLoader(true);
      try {
        const response = await testVendorConnectionSyncAPI(
          account,
          vendorData,
          editedSettings.autoSyncAssets,
          editedSettings.watermarkedPhotos,
          vendor.key,
        );
        if (!cancelled && response?.data?.status === 'Success') {
          setConnected(true);
          setConnectionState(connectedConnection());
        }
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } }).response?.status;
        if (!cancelled && status) {
          setConnected(false);
          setConnectionState(failedConnection());
          dispatch(showAlert({
            type: Variants.ERROR,
            message: formSubmitAttempted
              ? 'Connection failed.  Please check the credentials and try again.'
              : 'Connection failed. Update credentials to resume syncing.',
          }));
        }
      } finally {
        if (!cancelled) { setLoading(false); setToggleLoader(false); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorData]);

  if (loading) {
    return (
      <div className="flex w-full items-center justify-center bg-card">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <div className="px-4">
        {/* Prototype: the credentials tab opens on the guide line — shown in read
            mode too — and then the credential rows. There is no vendor-logo /
            connector / PlanOmatic block; the header's status label carries the
            connection state on its own. */}
        <div className="pb-4 pt-4">
          <Typography variant="body">
            {mode === 'read' ? 'Refer to the ' : 'Please fill in the details below. Refer to the '}
          </Typography>
          <Button type="button" variant="link" onClick={redirectToHowToConfigure}>
            guide
          </Button>
          <Typography variant="body">{' '}in the Integration Overview tab for step-by-step instructions.</Typography>
        </div>

        {mode === 'read' && vendorData ? (
          <CredentialSummary
            vendorName={vendorDisplayName}
            rows={credentialRows(credentialKindFor('rentvine', vendorData), vendorDisplayName, vendorData)}
            className="pb-2"
          />
        ) : (
          <>

            <Formik
              key={credentialsFormKey}
              validationSchema={buildSchema({
                key: Boolean(vendorData?.key),
                secret: Boolean(vendorData?.secret),
              })}
              // Normally blank (paste to replace the stored values). After a
              // rejected save the form is remounted carrying exactly what was
              // submitted, so the user can retry without re-pasting and the
              // retry sends the new credentials rather than blanks that would
              // merge the old stored ones back in (review finding 1).
              initialValues={{
                url: pendingCredentials?.url ?? vendorData?.url ?? '',
                key: pendingCredentials?.key ?? '',
                secret: pendingCredentials?.secret ?? '',
              }}
              validateOnBlur
              validateOnChange={false}
              onSubmit={(values) => submitCredentials(values)}
              // Safe here only because key/secret carry no stored value in
              // `initialValues` (blank, or what the user just submitted)
              // and every `vendorData` write in `useVendorSettings` also flips
              // `mode` (bootstrap → read, saveAndSync → read, rollback →
              // edit with a remount-free baseline), so no write reaches a
              // mounted edit form and reinitialization can never wipe what the
              // user is typing.
              enableReinitialize
            >
              {({ handleSubmit, handleChange, values, touched, errors }) => {
                submitRef.current = handleSubmit;
                const hasStored = vendorData !== null;
                return (
                  <Form id="vendor-configuration" noValidate onSubmit={handleSubmit} className="max-w-[520px]">
                    <FormikDirtyReporter onDirtyChange={setCredentialsDirty} />
                    <SubmitRefCleanup clear={() => { submitRef.current = null; }} />
                    <div className={connected ? '' : errorCls}>
                      <div className="relative mb-3">
                        <Label htmlFor="url" required>
                          {`${vendorDisplayName} subdomain`}
                        </Label>
                        <div className="flex items-center">
                          <Input id="url" name="url" type="text" placeholder="Enter Subdomain"
                            value={values.url} onChange={handleChange}
                            className={touched.url && errors.url ? 'border-destructive' : ''} data-testid="url_id" />
                          <span className="ml-2 whitespace-nowrap text-sm text-muted-foreground">
                            {`.${vendor.key}.com`}
                          </span>
                        </div>
                        <FormError message={touched.url ? errors.url : undefined} />
                      </div>
                    </div>
                    <FormField id="key" label="API Key"
                      placeholder={hasStored ? copy.editMode.pasteKey : 'Enter API Key'}
                      value={values.key} error={touched.key ? errors.key : undefined}
                      onChange={handleChange} connected={connected} required={!hasStored} />
                    <FormField id="secret" label="API Secret" type="password"
                      placeholder={hasStored ? copy.editMode.pasteSecret : 'Enter API Secret'}
                      value={values.secret} error={touched.secret ? errors.secret : undefined}
                      onChange={handleChange} connected={connected} required={!hasStored} />
                    {hasStored && (
                      <p className={cn(typographyVariants({ variant: 'body-sm' }), 'text-muted-foreground')}>
                        {copy.editMode.maskNote}
                      </p>
                    )}
                  </Form>
                );
              }}
            </Formik>
          </>
        )}
      </div>

      {showModel && (
        <Modal
          open={showModel}
          onOpenChange={(open) => !open && setShowModel(false)}
          size="sm"
          srOnlyTitle="Connection failed"
        >
          <div className="mb-12 flex flex-col items-center gap-3 text-center">
            <IconComponent src={connectionFailed} alt="Connection failed" width={250} height={250} />
            <Typography variant="heading-lg">Connection failed</Typography>
            <Typography variant="body">Please check the credentials and try again.</Typography>
            <Button type="button" onClick={() => setShowModel(false)}>Okay</Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default RentvineConnectionPanel;
